/* BRYVO theme — bryvo.js
   Vanilla, no dependencies. Every behaviour is opt-in via a data attribute so
   sections stay independent and the theme editor can add or remove them freely.
   ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var cfg = window.BRYVO || {};
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  var motionOK = function () { return !reduced.matches && cfg.level !== 'still'; };
  var fullMotion = function () { return motionOK() && cfg.level !== 'measured'; };

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* --- Header: measure, and hide on scroll down --------------------------- */
  function header(root) {
    var el = $('[data-header]', root);
    if (!el) return;

    var setH = function () {
      document.documentElement.style.setProperty('--hdr-h', el.offsetHeight + 'px');
    };
    setH();
    if (window.ResizeObserver) new ResizeObserver(setH).observe(el);

    var last = window.scrollY;
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.scrollY;
        var down = y > last && y > 240;
        if (!document.body.classList.contains('menu-open')) {
          el.classList.toggle('hdr--hidden', down);
        }
        last = y;
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --- Mobile menu -------------------------------------------------------- */
  function menu(root) {
    var btn = $('[data-burger]', root);
    var panel = $('[data-mnav]', root);
    if (!btn || !panel) return;

    var open = false;
    var lastFocus = null;
    if ('inert' in panel) panel.inert = true;

    function setOpen(next) {
      open = next;
      document.body.classList.toggle('menu-open', open);
      btn.setAttribute('aria-expanded', String(open));
      panel.setAttribute('aria-hidden', String(!open));
      // Keep the closed menu out of the tab order as well as the accessibility tree.
      if ('inert' in panel) panel.inert = !open;
      if (open) {
        lastFocus = document.activeElement;
        $$('a, button', panel).forEach(function (a, i) { a.style.setProperty('--md', (60 + i * 45) + 'ms'); });
        var first = $('a, button', panel);
        if (first) setTimeout(function () { first.focus(); }, 260);
      } else if (lastFocus) {
        lastFocus.focus();
      }
    }

    btn.addEventListener('click', function () { setOpen(!open); });

    document.addEventListener('keydown', function (e) {
      if (!open) return;
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab') return;
      var items = $$('a, button', panel).filter(function (n) { return n.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    panel.addEventListener('click', function (e) {
      if (e.target.closest('a')) setOpen(false);
    });

    window.addEventListener('resize', function () {
      if (open && window.innerWidth > 1099) setOpen(false);
    });
  }

  /* --- Reveal on scroll --------------------------------------------------- */
  var revealIO = null;
  function reveal(root) {
    if (cfg.reveal === false) return;
    // Only now is it safe for CSS to hide anything: if this script never runs,
    // every heading and section stays visible rather than blank.
    document.documentElement.classList.add('reveal-ready');
    if (!('IntersectionObserver' in window)) {
      $$('[data-reveal], .lines', root).forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealIO.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });
    }
    $$('[data-reveal], .lines', root).forEach(function (n) {
      // Stagger siblings that share a group so a row of cards arrives in order.
      var group = n.closest('[data-reveal-group]');
      if (group && !n.style.getPropertyValue('--rd')) {
        var sibs = $$('[data-reveal]', group);
        var i = sibs.indexOf(n);
        if (i > -1) n.style.setProperty('--rd', Math.min(i, 8) * 70 + 'ms');
      }
      revealIO.observe(n);
    });
  }

  /* --- Pointer parallax on layered hero fragments ------------------------- */
  function parallax(root) {
    if (cfg.pointer === false || !fullMotion() || !fine.matches) return;

    $$('[data-parallax]', root).forEach(function (scene) {
      var layers = $$('[data-depth]', scene);
      if (!layers.length) return;
      var raf = null;
      var tx = 0, ty = 0;

      function move(e) {
        var r = scene.getBoundingClientRect();
        tx = (e.clientX - (r.left + r.width / 2)) / r.width;
        ty = (e.clientY - (r.top + r.height / 2)) / r.height;
        if (raf) return;
        raf = requestAnimationFrame(apply);
      }
      function apply() {
        raf = null;
        layers.forEach(function (l) {
          var d = parseFloat(l.dataset.depth) || 0;
          l.style.transform = 'translate3d(' + (tx * d * 26).toFixed(2) + 'px,' + (ty * d * 26).toFixed(2) + 'px,0)';
        });
      }
      function reset() {
        layers.forEach(function (l) { l.style.transform = ''; });
      }

      scene.addEventListener('pointermove', move);
      scene.addEventListener('pointerleave', reset);
    });
  }

  /* --- Magnetic cards ----------------------------------------------------- */
  function magnetic(root) {
    if (cfg.pointer === false || !fullMotion() || !fine.matches) return;

    $$('[data-magnet]', root).forEach(function (el) {
      var strength = parseFloat(el.dataset.magnet) || 6;
      var raf = null, x = 0, y = 0;

      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        x = ((e.clientX - r.left) / r.width - 0.5) * strength;
        y = ((e.clientY - r.top) / r.height - 0.5) * strength;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          el.style.transform = 'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0)';
        });
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* --- Showcase tabs ------------------------------------------------------ */
  function tabs(root) {
    $$('[data-tabs]', root).forEach(function (group) {
      var buttons = $$('[role="tab"]', group);
      var panels = $$('[role="tabpanel"]', group);
      if (!buttons.length) return;

      function select(i, focus) {
        buttons.forEach(function (b, n) {
          b.setAttribute('aria-selected', String(n === i));
          b.tabIndex = n === i ? 0 : -1;
        });
        panels.forEach(function (p, n) {
          p.classList.toggle('is-active', n === i);
          p.hidden = n !== i;
        });
        if (focus) buttons[i].focus();
      }

      buttons.forEach(function (b, i) {
        b.addEventListener('click', function () { select(i); });
        b.addEventListener('keydown', function (e) {
          var n = null;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') n = (i + 1) % buttons.length;
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') n = (i - 1 + buttons.length) % buttons.length;
          if (e.key === 'Home') n = 0;
          if (e.key === 'End') n = buttons.length - 1;
          if (n === null) return;
          e.preventDefault();
          select(n, true);
        });
      });

      select(0);
    });
  }

  /* --- Accordion (FAQ) ---------------------------------------------------- */
  function accordion(root) {
    $$('[data-accordion]', root).forEach(function (group) {
      var single = group.dataset.accordion === 'single';
      var rows = $$('[data-acc-row]', group);

      rows.forEach(function (row) {
        var btn = $('[data-acc-q]', row);
        var panel = $('[data-acc-a]', row);
        if (!btn || !panel) return;

        function close(r) {
          var b = $('[data-acc-q]', r), p = $('[data-acc-a]', r);
          if (!b || b.getAttribute('aria-expanded') !== 'true') return;
          b.setAttribute('aria-expanded', 'false');
          p.style.height = p.scrollHeight + 'px';
          requestAnimationFrame(function () { p.style.height = '0px'; });
        }

        btn.addEventListener('click', function () {
          var expanded = btn.getAttribute('aria-expanded') === 'true';
          if (expanded) { close(row); return; }
          if (single) rows.forEach(function (r) { if (r !== row) close(r); });
          btn.setAttribute('aria-expanded', 'true');
          panel.style.height = panel.scrollHeight + 'px';
        });

        panel.addEventListener('transitionend', function (e) {
          if (e.propertyName !== 'height') return;
          if (btn.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
        });
      });

      window.addEventListener('resize', function () {
        rows.forEach(function (r) {
          var b = $('[data-acc-q]', r), p = $('[data-acc-a]', r);
          if (b && p && b.getAttribute('aria-expanded') === 'true') p.style.height = 'auto';
        });
      });
    });
  }

  /* --- Process progress --------------------------------------------------- */
  function process(root) {
    $$('[data-process]', root).forEach(function (list) {
      var steps = $$('[data-step]', list);
      var bar = $('[data-process-bar]', list.closest('[data-process-scope]') || document);
      if (!steps.length) return;

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { e.target.classList.toggle('is-in', e.isIntersecting); });
          if (!bar) return;
          var done = steps.filter(function (s) { return s.getBoundingClientRect().top < window.innerHeight * 0.6; }).length;
          bar.style.setProperty('--p', Math.round((done / steps.length) * 100) + '%');
        }, { rootMargin: '-35% 0px -35% 0px', threshold: 0 });
        steps.forEach(function (s) { io.observe(s); });
      } else {
        steps.forEach(function (s) { s.classList.add('is-in'); });
      }
    });
  }

  /* --- Marquee: duplicate the track so the loop is seamless --------------- */
  function marquee(root) {
    $$('[data-marquee]', root).forEach(function (m) {
      var track = $('[data-marquee-track]', m);
      if (!track || track.dataset.cloned === 'true') return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      m.appendChild(clone);
      track.dataset.cloned = 'true';
      clone.dataset.cloned = 'true';
    });
  }

  /* --- Cursor label ------------------------------------------------------- */
  function cursor() {
    if (cfg.cursor === false || !fine.matches || !fullMotion()) return;
    var el = $('[data-cursor]');
    if (!el) return;
    var text = $('[data-cursor-text]', el);
    var x = 0, y = 0, raf = null;

    function draw() {
      raf = null;
      el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
    }

    document.addEventListener('pointermove', function (e) {
      x = e.clientX + 16;
      y = e.clientY + 16;
      if (!raf) raf = requestAnimationFrame(draw);
    }, { passive: true });

    document.addEventListener('pointerover', function (e) {
      var t = e.target.closest('[data-cursor-hover]');
      if (!t) return;
      text.textContent = t.dataset.cursorHover || 'View';
      el.classList.add('is-on');
    });

    document.addEventListener('pointerout', function (e) {
      var t = e.target.closest('[data-cursor-hover]');
      if (t && !t.contains(e.relatedTarget)) el.classList.remove('is-on');
    });
  }

  /* --- Smooth in-page anchors -------------------------------------------- */
  function anchors(root) {
    $$('a[href^="#"]', root).forEach(function (a) {
      var id = a.getAttribute('href');
      if (!id || id === '#' || id.length < 2) return;
      a.addEventListener('click', function (e) {
        var target = document.getElementById(id.slice(1));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: motionOK() ? 'smooth' : 'auto', block: 'start' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }


  /* --- Crimson light that follows the pointer ----------------------------- */
  function spotlight() {
    var el = $('[data-spotlight]');
    if (!el) return;
    if (cfg.spotlight === false || !fine.matches || !fullMotion()) {
      el.style.transform = 'translate3d(50vw, 30vh, 0)';
      return;
    }

    var tx = window.innerWidth / 2;
    var ty = window.innerHeight * 0.34;
    var cx = tx, cy = ty;
    var running = false;

    function frame() {
      // Ease towards the pointer so the light drifts rather than snaps.
      cx += (tx - cx) * 0.06;
      cy += (ty - cy) * 0.06;
      el.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0)';
      if (Math.abs(tx - cx) > 0.4 || Math.abs(ty - cy) > 0.4) {
        requestAnimationFrame(frame);
      } else {
        running = false;
      }
    }

    document.addEventListener('pointermove', function (e) {
      tx = e.clientX;
      ty = e.clientY;
      if (!running) { running = true; requestAnimationFrame(frame); }
    }, { passive: true });

    el.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
  }

  /* --- Dim the light while a paper section fills the screen ---------------- */
  function schemeWatch(root) {
    if (!('IntersectionObserver' in window)) return;
    var panels = $$('.scheme-paper', root);
    if (!panels.length) return;
    var live = 0;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { live += e.isIntersecting ? 1 : -1; });
      live = Math.max(0, live);
      document.body.classList.toggle('scheme-paper-visible', live > 0);
    }, { threshold: 0.45 });
    panels.forEach(function (p) { io.observe(p); });
  }

  /* --- Scroll progress ---------------------------------------------------- */
  function progress() {
    var bar = $('[data-progress]');
    if (!bar || cfg.progress === false) return;
    var ticking = false;

    function draw() {
      ticking = false;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.setProperty('--p', max > 0 ? Math.min(1, window.scrollY / max) : 0);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(draw);
    }, { passive: true });
    draw();
  }

  /* --- First-load wipe ---------------------------------------------------- */
  function intro() {
    var el = $('[data-intro]');
    if (!el) return;
    if (!fullMotion()) { el.remove(); return; }
    // The element removes itself so it can never trap a click.
    setTimeout(function () { el.remove(); }, 1400);
  }

  /* --- Magnetic buttons --------------------------------------------------- */
  function magneticButtons(root) {
    if (cfg.pointer === false || !fullMotion() || !fine.matches) return;

    $$('.btn', root).forEach(function (el) {
      if (el.dataset.magnetic === 'true') return;
      el.dataset.magnetic = 'true';
      var raf = null, x = 0, y = 0;

      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        x = ((e.clientX - r.left) / r.width - 0.5) * 10;
        y = ((e.clientY - r.top) / r.height - 0.5) * 8;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          el.style.transform = 'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0)';
        });
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* --- Crimson rule that draws across a section on entry ------------------ */
  function sweeps(root) {
    if (!('IntersectionObserver' in window)) return;
    var els = $$('.sweep', root);
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -15% 0px' });
    els.forEach(function (e) { io.observe(e); });
  }

  /* --- Boot --------------------------------------------------------------- */
  function init(root) {
    root = root || document;
    cfg.ready = true;
    header(root);
    menu(root);
    reveal(root);
    parallax(root);
    magnetic(root);
    tabs(root);
    accordion(root);
    process(root);
    marquee(root);
    anchors(root);
    magneticButtons(root);
    sweeps(root);
    schemeWatch(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function boot() {
    init();
    cursor();
    spotlight();
    progress();
    intro();
  }

  // Shopify theme editor: re-bind when a section is added, moved or reloaded.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    e.target.querySelectorAll('[data-reveal], .lines').forEach(function (n) { n.classList.add('is-in'); });
  });
})();
